import { z } from 'zod';
import { GST_STATES, isValidGstin, isValidPincode } from './gst.js';
import { isValidSlug } from './slug.js';
/** Runtime validation for every untrusted boundary: callable Functions, the
 * CSV importer, and webhook payloads. Types in types.ts describe what we store;
 * these describe what we accept. */
export const slugSchema = z.string().refine(isValidSlug, 'Must be a lowercase hyphenated slug');
export const addressSchema = z.object({
    name: z.string().min(2).max(120),
    line1: z.string().min(3).max(200),
    line2: z.string().max(200).optional(),
    city: z.string().min(2).max(100),
    stateCode: z.string().refine((c) => c in GST_STATES, 'Unknown state code'),
    state: z.string().min(2).max(100),
    pincode: z.string().refine(isValidPincode, 'Invalid PIN code'),
    phone: z.string().regex(/^[6-9]\d{9}$/, 'Enter a 10-digit Indian mobile number'),
    gstin: z
        .string()
        .optional()
        .refine((g) => !g || isValidGstin(g), 'Invalid GSTIN'),
});
export const printSpecSchema = z.object({
    trimWidthMm: z.number().positive().max(10_000),
    trimHeightMm: z.number().positive().max(10_000),
    bleedMm: z.number().min(0).max(50),
    safeMarginMm: z.number().min(0).max(100),
    colorMode: z.enum(['CMYK', 'RGB']),
    minDpi: z.number().int().min(72).max(1200),
    sides: z.union([z.literal(1), z.literal(2)]),
    dielineImageId: z.string().optional(),
});
export const selectedOptionSchema = z.object({
    groupId: z.string().min(1),
    groupName: z.string().min(1),
    valueId: z.string().min(1),
    valueName: z.string().min(1),
});
export const cartLineSchema = z.object({
    productId: z.string().min(1),
    qty: z.number().int().positive().max(1_000_000),
    selectedOptions: z.array(selectedOptionSchema).max(20),
    /** Set when the customer designed online instead of uploading artwork. */
    designId: z.string().min(1).max(128).optional(),
    /** Set when the line is a saved print-lab (imposition) job. The rendered
     * sheet is derived server-side from the job id — the path is never trusted. */
    impositionJobId: z.string().min(1).max(128).optional(),
    /** Set when the customer chose an artist's work from the marketplace. Only
     * the id travels: the price uplift, the royalty and the artist's identity are
     * all read from the artwork server-side, never sent by the client. */
    artworkId: z.string().min(1).max(128).optional(),
    /** A pack line: one published artwork per slot. Checked against the product's pack size server-side. */
    artworkIds: z.array(z.string().min(1).max(128)).min(2).max(24).optional(),
    /** Chosen size for a size-variable product; the server recomputes the area
     * multiplier from the product's own bounds, so these are only a request. */
    sizeWmm: z.number().positive().max(10_000).optional(),
    sizeHmm: z.number().positive().max(10_000).optional(),
    /** Per-size quantities for an apparel (size-grid) line, keyed by size value
     * id. The server re-sums to the line quantity and re-prices — never trusted. */
    sizeQuantities: z.record(z.string().max(64), z.number().int().nonnegative().max(1_000_000)).optional(),
    /** Optional free-text print instructions for this line. Plain text, capped;
     * never affects price. */
    printInstructions: z.string().max(1000).optional(),
    /**
     * Which positions on the garment are decorated, and by which method.
     *
     * Only the position id and the method travel. The surcharge, the maximum
     * size and whether the position exists at all are read from the PRODUCT
     * server-side — a client that could name its own placement could also name
     * its own price. Capped because a line has a handful of positions, not a
     * thousand, and the cap is what stops a payload of them.
     */
    decorations: z
        .array(z.object({
        placementId: z.string().min(1).max(64),
        method: z.enum(['print', 'embroidery']),
    }))
        .max(8)
        .optional(),
    /** Artwork uploaded BEFORE checkout (createCartArtworkUploadUrl). Carries only
     * the server-minted ref + filename; the path is re-derived server-side, never
     * trusted from the client. Checkout copies it into the order. */
    uploadedArtwork: z
        .object({
        ref: z.string().min(8).max(80),
        filename: z.string().min(1).max(200),
        contentType: z.string().min(1).max(120),
    })
        .optional(),
});
/**
 * What the browser sends to the createOrder Function. Deliberately does NOT
 * include any price — the server recomputes every figure from the catalog.
 */
export const createOrderInputSchema = z.object({
    lines: z.array(cartLineSchema).min(1).max(50),
    shippingAddress: addressSchema,
    billingAddress: addressSchema.optional(),
    couponCode: z.string().max(40).optional(),
    /**
     * The artist whose link brought this visitor, read from the `eps_ref` cookie
     * by the checkout client.
     *
     * Sent by the browser rather than read server-side because checkout is a
     * CALLABLE, and callables receive no cookies. That means it is untrusted
     * input: it is validated as a handle, resolved to a real active designer, and
     * recorded as attribution only. It can never move money, choose a price, or
     * qualify anything on its own.
     */
    referralHandle: z.string().max(30).optional(),
    /**
     * Where the visitor came from, captured on the first page of their visit.
     *
     * Deliberately loose here and tightened later: `sanitiseAttribution` clips
     * every field and RE-DERIVES the channel from the referrer, so the shape only
     * has to be roughly right at this boundary. Rejecting a checkout because an
     * analytics field was malformed would be the wrong trade entirely.
     */
    attribution: z.unknown().optional(),
    /** Apply the customer's Print Credits to this order. The amount is derived
     * server-side from their real balance and the per-order cap — the client only
     * asks; it never sends a credit amount. */
    useCredits: z.boolean().optional(),
    /**
     * When the customer ticked the terms box at checkout, in epoch millis.
     *
     * Recorded because acceptance only has evidential weight if there is a record
     * of it. Client-supplied and therefore not trusted as proof on its own — the
     * server clamps it to its own clock below and stores that. Optional so an
     * older client cannot be locked out mid-deploy.
     */
    termsAcceptedAt: z.number().int().positive().optional(),
    customerPhone: z.string().regex(/^[6-9]\d{9}$/).optional(),
    /** The email typed at checkout. Guest checkout has no auth token, so without
     * this the address is collected, validated and then discarded — leaving the
     * order and the CRM contact with no way to reach the customer. A signed-in
     * caller's token email still wins over anything sent here. */
    customerEmail: z.string().email().max(200).optional(),
});
export const optionValueWriteSchema = z.object({
    name: z.string().min(1).max(120),
    modifierType: z.enum(['flat', 'perUnit', 'percent']),
    modifierValue: z.number(),
    sku: z.string().max(60).optional(),
    imageId: z.string().optional(),
    swatchHex: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    isDefault: z.boolean().optional(),
    sortOrder: z.number().int().min(0),
    printSpecOverride: printSpecSchema.partial().optional(),
    status: z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']),
});
export const priceTierWriteSchema = z.object({
    qty: z.number().int().positive(),
    priceMinor: z.number().int().nonnegative(),
    optionKey: z.string().min(1),
    badge: z.string().max(40).optional(),
});
export const productWriteSchema = z.object({
    slug: slugSchema,
    name: z.string().min(2).max(200),
    // min(0): an unlisted product (see Product.unlisted) legitimately has none.
    categoryIds: z.array(z.string().min(1)).max(10),
    unlisted: z.boolean().optional(),
    shortDescription: z.string().max(300).optional(),
    description: z.string().max(20_000).optional(),
    highlights: z.array(z.string().max(200)).max(10).optional(),
    imageIds: z.array(z.string()).max(20),
    printSpec: printSpecSchema,
    basePriceMinor: z.number().int().nonnegative(),
    minLineChargeMinor: z.number().int().nonnegative().optional(),
    minQty: z.number().int().positive(),
    maxQty: z.number().int().positive().optional(),
    allowCustomQty: z.boolean(),
    turnaroundDays: z.number().int().min(0).max(90),
    hsnCode: z.string().regex(/^\d{4,8}$/).optional(),
    gstRatePercent: z.number().min(0).max(28),
    seo: z
        .object({
        title: z.string().max(120).optional(),
        description: z.string().max(320).optional(),
        keywords: z.array(z.string().max(60)).max(20).optional(),
        canonicalPath: z.string().max(300).optional(),
    })
        .optional(),
    status: z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']),
});
export const categoryWriteSchema = z.object({
    slug: slugSchema,
    name: z.string().min(2).max(120),
    parentId: z.string().nullable(),
    level: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    sortOrder: z.number().int().min(0),
    description: z.string().max(5_000).optional(),
    heroImageId: z.string().optional(),
    status: z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']),
});
export const vendorWriteSchema = z.object({
    name: z.string().min(2).max(200),
    contactPerson: z.string().min(2).max(120),
    phone: z.string().regex(/^[6-9]\d{9}$/),
    email: z.string().email(),
    gstin: z
        .string()
        .optional()
        .refine((g) => !g || isValidGstin(g), 'Invalid GSTIN'),
    address: addressSchema.optional(),
    capabilities: z.array(z.string()).max(100),
    turnaroundDays: z.number().int().min(0).max(90),
    rateCard: z
        .array(z.object({
        targetId: z.string().min(1),
        targetType: z.enum(['product', 'category']),
        unitCostMinor: z.number().int().nonnegative(),
        minQty: z.number().int().positive().optional(),
    }))
        .max(500),
    status: z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']),
});
export const assignVendorInputSchema = z.object({
    orderId: z.string().min(1),
    assignments: z
        .array(z.object({
        orderItemId: z.string().min(1),
        vendorId: z.string().min(1),
        vendorCostMinor: z.number().int().nonnegative().optional(),
    }))
        .min(1)
        .max(50),
    note: z.string().max(1000).optional(),
});
export const vendorJobUpdateSchema = z.object({
    orderId: z.string().min(1),
    orderItemId: z.string().min(1),
    to: z.enum(['ACCEPTED', 'DECLINED', 'IN_PRODUCTION', 'READY', 'DISPATCHED']),
    note: z.string().max(1000).optional(),
    courier: z.string().max(120).optional(),
    trackingNumber: z.string().max(120).optional(),
    proofImageIds: z.array(z.string()).max(10).optional(),
});
/** Editing a line's print instructions after the order exists (artwork page). */
export const setInstructionsInputSchema = z.object({
    orderId: z.string().min(1).max(128),
    orderItemId: z.string().min(1).max(128),
    printInstructions: z.string().max(1000),
});
export const setRoleInputSchema = z.object({
    uid: z.string().min(1),
    role: z.enum(['customer', 'admin', 'vendor', 'designer']),
    vendorId: z.string().min(1).optional(),
    designerId: z.string().min(1).optional(),
});
/** The loyalty programme's rules, as written from the admin config page. The
 * earn rate lives here and only here — this doc is never world-readable. */
export const loyaltyTierConfigSchema = z.object({
    tier: z.enum(['SILVER', 'GOLD', 'PLATINUM']),
    earnRateBps: z.number().int().min(0).max(2000), // ≤ 20%, a guardrail against a fat-fingered rate
    minSpend12mMinor: z.number().int().nonnegative(),
    label: z.string().min(1).max(40),
    perks: z.array(z.string().max(120)).max(8),
});
export const loyaltyConfigWriteSchema = z.object({
    tiers: z.array(loyaltyTierConfigSchema).length(3),
    earnBase: z.enum(['NET_ITEMS_EX_TAX', 'GRAND_TOTAL']),
    redeemCapBps: z.number().int().min(0).max(10000),
    minRedeemMinor: z.number().int().nonnegative(),
    expiryMonths: z.number().int().min(1).max(120),
});
//# sourceMappingURL=schemas.js.map